import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, ExternalLink } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function PolitiqueConfidentialite() {
  return (
    <div className="min-h-screen bg-[#1a1c1b] text-[#dfddd6]" data-testid="page-politique-confidentialite">
      <Header />
      <main className="max-w-4xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <Link to="/" className="inline-flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors mb-8 text-sm">
          <ArrowLeft size={16} /> Retour à l'accueil
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-[#d9fb06]/10 rounded-full flex items-center justify-center">
            <Shield className="text-[#d9fb06]" size={24} />
          </div>
          <h1 className="font-black text-4xl md:text-5xl text-[#d9fb06] uppercase">Politique de confidentialité</h1>
        </div>
        <p className="text-[#888680] mb-10 text-sm">Dernière mise à jour : 21 juillet 2026</p>

        <div className="space-y-8">

          <Section title="1. Responsable du traitement">
            <p>ValidatedBoat, dont les coordonnées complètes figurent dans les <Link to="/mentions-legales" className="text-[#d9fb06] underline">mentions légales</Link>, est responsable du traitement des données personnelles collectées sur le site validatedboat.fr.</p>
          </Section>

          <Section title="2. Données collectées">
            <p>Nous collectons uniquement les données strictement nécessaires à nos services :</p>
            <ul className="list-disc list-inside space-y-2 mt-3 text-[#dfddd6]">
              <li><strong>Formulaire d'estimation</strong> : nom, email, téléphone, type de bateau, année, longueur, état</li>
              <li><strong>Demande de devis</strong> : nom, email, téléphone, service souhaité, message</li>
              <li><strong>Newsletter</strong> : adresse email</li>
              <li><strong>Paiement</strong> : nom, email, adresse (traité par Stripe — nous ne stockons aucune donnée bancaire)</li>
              <li><strong>Mesure d'audience</strong> (avec consentement uniquement) : pages visitées, appareil, navigateur, durée (via PostHog)</li>
            </ul>
          </Section>

          <Section title="3. Finalités & base légale">
            <div className="overflow-x-auto mt-3">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-[#302f2c]">
                    <th className="p-3 text-left text-[#d9fb06] border border-[#3f4816]">Finalité</th>
                    <th className="p-3 text-left text-[#d9fb06] border border-[#3f4816]">Base légale</th>
                    <th className="p-3 text-left text-[#d9fb06] border border-[#3f4816]">Durée</th>
                  </tr>
                </thead>
                <tbody className="text-[#dfddd6]">
                  <TR l="Répondre à une demande de contact / estimation" b="Intérêt légitime" d="3 ans" />
                  <TR l="Exécution d'une prestation payante" b="Contrat" d="10 ans (facturation)" />
                  <TR l="Envoi de newsletter" b="Consentement" d="Jusqu'à désinscription" />
                  <TR l="Mesure d'audience (PostHog)" b="Consentement" d="12 mois" />
                  <TR l="Respect obligations légales/fiscales" b="Obligation légale" d="10 ans" />
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="4. Destinataires des données">
            <p>Vos données sont accessibles uniquement à ValidatedBoat et à nos sous-traitants strictement nécessaires :</p>
            <ul className="list-disc list-inside space-y-1 mt-3 text-[#dfddd6]">
              <li><strong>Resend</strong> (envoi d'emails) — DPA signé, serveurs UE</li>
              <li><strong>Stripe</strong> (traitement paiements) — DPA signé, PCI-DSS Level 1</li>
              <li><strong>MongoDB Atlas</strong> (hébergement base de données) — DPA signé, serveurs UE</li>
              <li><strong>PostHog</strong> (analytique, avec consentement) — DPA signé, hébergement US avec SCC</li>
              <li><strong>Emergent</strong> (hébergement de l'application) — DPA signé</li>
            </ul>
            <p className="mt-3">Vos données ne sont jamais vendues ni louées à des tiers.</p>
          </Section>

          <Section title="5. Transferts hors UE">
            <p>PostHog héberge ses serveurs aux États-Unis. Ce transfert s'effectue dans le cadre des <strong>clauses contractuelles types (SCC)</strong> approuvées par la Commission européenne, garantissant un niveau de protection équivalent au RGPD.</p>
          </Section>

          <Section title="6. Vos droits (RGPD)">
            <p>Vous disposez à tout moment des droits suivants sur vos données :</p>
            <ul className="list-disc list-inside space-y-1 mt-3 text-[#dfddd6]">
              <li>Droit d'<strong>accès</strong> : obtenir une copie de vos données</li>
              <li>Droit de <strong>rectification</strong> : corriger vos données</li>
              <li>Droit à l'<strong>effacement</strong> ("droit à l'oubli")</li>
              <li>Droit à la <strong>portabilité</strong> : récupérer vos données en format lisible</li>
              <li>Droit d'<strong>opposition</strong> au traitement</li>
              <li>Droit à la <strong>limitation</strong> du traitement</li>
              <li>Droit d'<strong>introduire une réclamation</strong> auprès de la CNIL (<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#d9fb06] underline inline-flex items-center gap-1">cnil.fr <ExternalLink size={12} /></a>)</li>
            </ul>
            <Link
              to="/exercer-mes-droits"
              data-testid="link-exercer-droits"
              className="inline-block mt-5 bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-6 py-3 font-bold uppercase text-sm"
            >
              Exercer mes droits
            </Link>
          </Section>

          <Section title="7. Sécurité">
            <p>Nous mettons en œuvre des mesures techniques et organisationnelles adaptées : HTTPS/TLS, cloisonnement des accès admin, chiffrement des mots de passe, audit régulier de nos prestataires.</p>
          </Section>

          <Section title="8. Cookies">
            <p>Le détail des cookies utilisés est disponible dans notre <Link to="/politique-cookies" className="text-[#d9fb06] underline">politique cookies</Link>.</p>
          </Section>

          <Section title="9. Contact">
            <p>Pour toute question relative à vos données, contactez-nous via le <Link to="/exercer-mes-droits" className="text-[#d9fb06] underline">formulaire dédié</Link> ou par email à l'adresse indiquée dans nos <Link to="/mentions-legales" className="text-[#d9fb06] underline">mentions légales</Link>.</p>
          </Section>

        </div>
      </main>
      <Footer />
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="bg-[#302f2c] border border-[#3f4816] rounded-lg p-6">
    <h2 className="text-xl md:text-2xl font-bold text-[#d9fb06] mb-3">{title}</h2>
    <div className="text-[#dfddd6] leading-relaxed">{children}</div>
  </section>
);

const TR = ({ l, b, d }) => (
  <tr>
    <td className="p-3 border border-[#3f4816]">{l}</td>
    <td className="p-3 border border-[#3f4816]">{b}</td>
    <td className="p-3 border border-[#3f4816]">{d}</td>
  </tr>
);
